import type { ProbeOutcome, ProbeResponse } from '@nadoutong/contracts';
import type { I18nKey } from '../i18n/index.ts';
import { useT } from '../i18n/index.ts';
import { formatFineUsdCents } from './formatters.ts';
import { StatusChip } from './StatusChip.tsx';

/**
 * Renders a single probe result. Shared between the probe modal and the
 * detail page's probe history log.
 *
 * Cost display rule: show raw USD with 6 decimal places. Probe amounts
 * are sub-cent; rounding to 2 decimals hides real accounting motion.
 */
interface Props {
  readonly result: ProbeResponse;
  readonly testIdPrefix?: string;
}

const OUTCOME_VARIANT: Record<ProbeOutcome, 'ok' | 'warning' | 'error' | 'neutral'> = {
  pass: 'ok',
  fail_shape: 'error',
  fail_content: 'error',
  fail_upstream_error: 'error',
  fail_timeout: 'warning',
};

const OUTCOME_LABEL_KEY: Record<ProbeOutcome, I18nKey> = {
  pass: 'probe.outcome.pass',
  fail_shape: 'probe.outcome.fail_shape',
  fail_content: 'probe.outcome.fail_content',
  fail_upstream_error: 'probe.outcome.fail_upstream_error',
  fail_timeout: 'probe.outcome.fail_timeout',
};

export function ProbeResultPanel({ result, testIdPrefix = 'probe-result' }: Props): JSX.Element {
  const t = useT();
  const variant = OUTCOME_VARIANT[result.outcome];
  return (
    <section
      data-testid={testIdPrefix}
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <StatusChip
            variant={variant}
            label={t(OUTCOME_LABEL_KEY[result.outcome])}
            testId={`${testIdPrefix}-outcome`}
          />
          <span className="font-mono text-[11px] text-outline">
            {result.vendor}/{result.model} · {result.protocol}
          </span>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
          {t('probe.id_label', { id: result.probeId.slice(0, 8) })}
        </span>
      </header>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <div className="flex flex-col gap-1">
          <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('probe.latency')}
          </dt>
          <dd
            className="font-mono text-sm text-on-surface"
            data-testid={`${testIdPrefix}-latency`}
          >
            {result.latencyMs} ms
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('probe.cost')}
          </dt>
          <dd className="font-mono text-sm text-on-surface" data-testid={`${testIdPrefix}-cost`}>
            {formatFineUsdCents(result.costUsdCents)}
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('probe.upstream_status')}
          </dt>
          <dd className="font-mono text-sm text-on-surface">
            {result.upstreamStatus === 0 ? '—' : result.upstreamStatus}
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('probe.input_output_tokens')}
          </dt>
          <dd className="font-mono text-sm text-on-surface">
            {result.usage.input_tokens} / {result.usage.output_tokens}
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('probe.cache_read_write')}
          </dt>
          <dd className="font-mono text-sm text-on-surface">
            {result.usage.cache_read_tokens} / {result.usage.cache_write_tokens}
          </dd>
        </div>
        {result.chargeSkipped !== undefined ? (
          <div className="col-span-2 flex flex-col gap-1 sm:col-span-4">
            <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('probe.charge_skipped')}
            </dt>
            <dd className="font-mono text-sm text-tertiary">{result.chargeSkipped}</dd>
          </div>
        ) : null}
        {result.error !== undefined && result.error.length > 0 ? (
          <div className="col-span-2 flex flex-col gap-1 sm:col-span-4">
            <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('probe.error')}
            </dt>
            <dd className="text-sm text-error" data-testid={`${testIdPrefix}-error`}>
              {result.error}
            </dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
}
